<template>
  <div @click="handleClose">
    <div :class="`${prefixCls}-content`" @click.stop>
      <div :class="`${prefixCls}-input__wrapper`">
        <a-input
          :class="`${prefixCls}-input`"
          :placeholder="t('common.searchText')"
          ref="inputRef"
          allow-clear
          :bordered="false"
          @blur="handleBlur"
          @focus="handleFocus"
          @change="handleChange"
          @pressEnter="handleChange"
        >
          <template #prefix>
            <SearchOutlined :style="{ fontSize: '16px', color: '#BFBFBF' }" />
          </template>
        </a-input>
      </div>

      <div
        :class="`${prefixCls}-not-data`"
        v-show="searchResult.length == 0 && keyword && isSearch"
      >
        {{ t('component.app.searchNotData') }}
      </div>

      <ul :class="`${prefixCls}-list`" v-show="!getIsNotData && isSearch" ref="scrollWrap">
        <div class="text-sm py-2"
          ><ExclamationCircleOutlined :style="{ color: '#999' }" />共为您找到
          <span class="num">{{ searchResult.length }}</span> 条搜索结果</div
        >
        <li
          :ref="setRefs(index)"
          v-for="(item, index) in searchResult"
          :key="item.path"
          :data-index="index"
          @mouseenter="handleMouseenter"
          @click.stop="handleItemClick(index)"
          :class="[
            `${prefixCls}-list__item`,
            {
              [`${prefixCls}-list__item--active`]: activeIndex === index,
            },
          ]"
        >
          <div :class="`${prefixCls}-list__item-text`">
            {{ item.name }}
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { computed, unref, ref } from 'vue';
  import { SearchOutlined, ExclamationCircleOutlined } from '@ant-design/icons-vue';
  import { useDesign } from '/@/hooks/web/useDesign';
  import { useRefs } from '/@/hooks/core/useRefs';
  import { useMenuSearch } from './useMenuSearch';
  import { useI18n } from '/@/hooks/web/useI18n';
  const emit = defineEmits(['close']);

  const scrollWrap = ref(null);
  const isSearch = ref(true);
  const inputRef = ref<Nullable<HTMLElement>>(null);

  const { t } = useI18n();
  const { prefixCls } = useDesign('app-search-modal');
  const [refs, setRefs] = useRefs();

  const { handleSearch, searchResult, keyword, activeIndex, handleEnter, handleMouseenter } =
    useMenuSearch(refs, scrollWrap, emit);
  function handleItemClick(index: number) {
    activeIndex.value = index; // 更新当前选中索引
    handleEnter(); // 调用跳转方法
    searchResult.value = [];
    keyword.value = '';
    isSearch.value = true;
  }

  function handleChange(e) {
    keyword.value = e.target.value;
    const mockEvent = {
      target: { value: keyword.value },
      stopPropagation: () => {}, // 空函数
    } as unknown as ChangeEvent;
    handleSearch(mockEvent);
  }

  function handleBlur() {
    const mockEvent = {
      target: { value: keyword.value },
      stopPropagation: () => {}, // 空函数
    } as unknown as ChangeEvent;
    handleSearch(mockEvent);
    let setTime = setTimeout(() => {
      isSearch.value = false;
      clearTimeout(setTime);
    }, 200);
  }

  function handleFocus() {
    isSearch.value = true;
    // 如果有事件对象，则尝试停止传播
    if (keyword.value) {
      const mockEvent = {
        target: { value: keyword.value },
        stopPropagation: () => {}, // 空函数
      } as unknown as ChangeEvent;
      handleSearch(mockEvent); // 如果已有内容，聚焦时重新搜索
    }
  }
  const getIsNotData = computed(() => !keyword || unref(searchResult).length === 0);
  function handleClose() {
    searchResult.value = [];
    isSearch.value = true;
    emit('close');
  }
</script>

<style lang="less" scoped>
  @prefix-cls: ~'@{namespace}-app-search-modal';
  @footer-prefix-cls: ~'@{namespace}-app-search-footer';

  // .vben-app-search-modal-content{
  //   // display: contents;
  // }

  html[data-theme='dark'] {
    // .@{prefix-cls} {
        // border: 1px solid rgb(196 188 188);
    .@{prefix-cls}-list,.@{prefix-cls}-not-data{
      background-color: #151515;
      border: 1px solid #444;
    }

    .vben-app-search-modal-input__wrapper{
      background-color: #151515;
    }
  }
  .@{prefix-cls} {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 800;
    display: flex;
    width: 100%;
    height: 100%;
    padding-top: 50px;
    background-color: rgb(0 0 0 / 25%);
    justify-content: center;

    &--mobile {
      padding: 0;

      > div {
        width: 100%;
      }

      .@{prefix-cls}-input {
        width: calc(100% - 38px);
      }

      .@{prefix-cls}-cancel {
        display: inline-block;
      }

      .@{prefix-cls}-content {
        width: 100%;
        height: 100%;
        border-radius: 0;
      }

      .@{footer-prefix-cls} {
        display: none;
      }

      .@{prefix-cls}-list {
        height: calc(100% - 80px);
        max-height: unset;

        &__item {
          &-enter {
            opacity: 0% !important;
          }
        }
      }

      // 移动端保持原有样式
      .@{prefix-cls}-input__wrapper {
        width: 100%;
        transition: none;
      }
    }

    &-content {
      position: relative;
      width: 300px;
      margin: 10px auto auto;
      flex-direction: column;
    }

    &-input__wrapper {
      display: flex;
      // justify-content: space-between;
      // align-items: center;
      justify-self: flex-end;
      border-radius: 4px;
      background: #f5f5f5;
      width: 200px; // 默认宽度200px
      // margin: 0 auto; // 居中显示
      transition: all 0.3s ease; // 添加过渡效果

      // 聚焦时向左延伸为300px
      &:focus-within {
        width: 300px;
      }
    }

    &-input {
      width: 100%;
      height: 36px;
      font-size: 1.5em;
      color: #1c1e21;
      border-radius: 6px;
      transition: all 0.3s ease; // 添加过渡效果

      span[role='img'] {
        color: #999;
      }
    }

    &-cancel {
      display: none;
      font-size: 1em;
      color: #666;
    }

    &-not-data {
      position: relative;
      z-index: 99;
      display: flex;
      width: 100%;
      height: 100px;
      align-items: center;
      justify-content: center;
      background-color: #fff;
      box-shadow: 0px 2px 12px 0px rgba(0,0,0,0.13);
      border-radius: 8px;
      margin-top: 5px;
      font-weight: 400;
      font-size: 14px;
      color: #999999;
    }

    [data-theme="dark"] &-list {
      border: 1px solid #444;

      .num{
        color: #fff;
      }

      &__item{
        &--active {
          color: #fff;
          background-color: #333;
        }
      }
    }

    &-list {
      position: relative;
      z-index: 999;
      max-height: 472px;
      margin: 0 auto;
      margin-top: 14px;
      overflow: auto;
      background: #FFFFFF;
      box-shadow: 0px 2px 12px 0px rgba(0,0,0,0.13);
      border-radius: 8px;

      .text-sm {
        color: #999;
        padding: 14px;

      }

      .num{
        color: #333;
      }

      &__item {
        position: relative;
        display: flex;
        width: 100%;
        height: 50px;
        // padding-left: 14px;
        font-size: 14px;
        color: @text-color-base;
        cursor: pointer;
        background-color: @component-background;
        // border-radius: 4px;
        align-items: center;
        padding: 0px 24px;

        > div:first-child,
        > div:last-child {
          display: flex;
          align-items: center;
        }

        &--active {
          // background-color: @primary-color;
          background-color: #f5f5f5;
          color: #333333;

          .@{prefix-cls}-list__item-enter {
            opacity: 100%;
          }
        }

        &-icon {
          width: 30px;
        }

        &-text {
          flex: 1;
        }

        &-enter {
          width: 30px;
          opacity: 0%;
        }
      }
    }
  }
</style>
